import { ArrowRight, Calendar, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/lib/site";

const Hero = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();

    const goToAnalyse = () => navigate("/analyse");
    const goToBooking = () => navigate("/kennenlerngespraech");

    return (
        <section id="hero" data-testid="hero-section" className="relative overflow-hidden pt-32 md:pt-40 pb-24 md:pb-32">
            {/* Background */}
            <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-ella-cream via-ella-sand to-ella-cream" />
                <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle_at_center,rgba(193,154,107,0.22),transparent_60%)] animate-soft-float" />
                <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle_at_center,rgba(31,29,28,0.08),transparent_60%)]" />
            </div>

            <div className="ella-container grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="inline-flex items-center gap-3" data-testid="hero-overline">
                        <span className="gold-hairline" />
                        <span className="ella-overline">{t.hero.overline}</span>
                        <span className="gold-hairline" />
                    </div>

                    <h1 className="ella-headline text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] mt-8 text-ella-ink" data-testid="hero-title">
                        <span className="block">{t.hero.title_a}</span>
                        <span className="block ella-gold-text italic font-light">{t.hero.title_b}</span>
                        <span className="block">{t.hero.title_c}</span>
                    </h1>

                    <p className="mt-8 max-w-xl text-lg text-ella-ink/75 leading-relaxed font-light" data-testid="hero-description">
                        {t.hero.description}
                    </p>

                    <p className="mt-6 font-display text-xl md:text-2xl text-ella-ink/90 italic max-w-lg" data-testid="hero-highlight">
                        — {t.hero.highlight}
                    </p>

                    <div className="mt-10 flex flex-wrap gap-3">
                        <button onClick={goToAnalyse} className="btn-primary" data-testid="hero-cta-apply">
                            {t.hero.cta_apply}
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <a href={SITE.contact.whatsappUrl} target="_blank" rel="noreferrer" className="btn-outline-gold" data-testid="hero-cta-whatsapp">
                            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                            {t.hero.cta_whatsapp}
                        </a>
                        <button onClick={goToBooking} className="btn-ghost" data-testid="hero-cta-book">
                            <Calendar className="w-4 h-4" strokeWidth={1.5} />
                            {t.hero.cta_book}
                        </button>
                    </div>

                    <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.24em] text-ella-muted">
                        <span className="flex items-center gap-2" data-testid="hero-badge-1"><span className="w-1.5 h-1.5 rounded-full bg-ella-gold" />{t.hero.badge_1}</span>
                        <span className="flex items-center gap-2" data-testid="hero-badge-2"><span className="w-1.5 h-1.5 rounded-full bg-ella-gold" />{t.hero.badge_2}</span>
                        <span className="flex items-center gap-2" data-testid="hero-badge-3"><span className="w-1.5 h-1.5 rounded-full bg-ella-gold" />{t.hero.badge_3}</span>
                    </div>
                </div>

                <div className="lg:col-span-5 relative" data-testid="hero-visual">
                    <div className="relative max-w-md mx-auto">
                        <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-ella-gold/30 to-ella-ink/10 blur-2xl" />
                        <div className="relative rounded-[2rem] overflow-hidden border border-ella-line shadow-[0_30px_80px_rgba(31,29,28,0.14)] aspect-[4/5]">
                            <img src={SITE.images.coachPortrait} alt="ELLA Online Coaching — Transformation" className="w-full h-full object-cover" />
                            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-ella-ink/70 via-ella-ink/20 to-transparent">
                                <div className="bg-ella-cream/90 backdrop-blur-md rounded-2xl px-5 py-4 border border-white/60">
                                    <p className="font-display text-xl text-ella-ink leading-none">Premium Transformation</p>
                                    <p className="ella-overline mt-1.5">Ein Coaching. Alle Bereiche.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
